<template>
    <div :class="classes">
        <div class="notification-item-icon" @click="clicked">
            <status-icon :icon="notification.icon" :color-scheme="notification.icon_colour" />
        </div>
        <div class="notification-body" @click="clicked">
            <h1>{{ notification.title }}</h1>
            <div class="notification-body-text">{{ notification.body }}</div>
            <div class="notification-meta">
                <div>{{ notification.timestamp | relativeDate }}</div>
                <div>
                    <div v-if="notification.actor">
                        <avatar :size="24" :user="notification.actor" /> {{ notification.actor.name }}
                    </div>
                </div>
            </div>
            <ui-icon-button
                v-if="!isToast"
                class="notification-action-button"
                ref="actionButton"
                has-dropdown
                icon="more_vert"
                @click="isActive = true"
                @dropdown-close="isActive = false"
                :append-dropdown-to-body="false"
                :loading="busy"
            >
                 <ui-menu
                    ref="actionMenu"
                    contain-focus
                    has-icons
                    slot="dropdown"
                    :options="options"
                    @select="actionSelected"
                    @close="$refs.actionButton.closeDropdown(); isActive = false"
                ></ui-menu>
            </ui-icon-button>
        </div>
        <div v-if="!isToast" class="notification-status" @click="clicked">
            <div></div>
        </div>
        <div v-if="isToast" class="notification-handle">
            <div>
                <ui-icon-button ref="closeButton" icon="close" @click="$emit('close')"></ui-icon-button>
            </div>
        </div>
    </div>
</template>

<script>
    import StatusIcon from "./StatusIcon"
    import filters from "../../filters";

    export default {
        components: { StatusIcon },
        props: {
            data: {
                type: Object,
                required: true
            },
            isToast: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                isActive: false,
                busy: false,
            }
        },
        filters,
        computed: {
            options() {
                let options = []

                if (this.notification.read) {
                    options.push({
                        id: 'mark-unread',
                        label: 'Mark as unread',
                    })
                } else {
                    options.push({
                        id: 'mark-read',
                        label: 'Mark as read',
                    })
                }

                options.push({
                    id: 'remove',
                    label: 'Remove'
                })

                return options
            },
            classes() {
                let classes = ['notification-item']

                if (this.isActive) {
                    classes.push('is-active')
                }

                if (this.notification.read) {
                    classes.push('notification-read')
                }

                if (this.notification.seen) {
                    classes.push('notification-seen')
                }

                return classes
            },
            notification() {
                let data = this.data.data
                let body
                let icon
                let icon_colour
                let title
                let actor

                switch (data.type)
                {
                    case 'deal.stacked':
                        return {
                            title: 'Deal Stacked',
                            icon: 'monetization_on',
                            icon_colour: 'info',
                            body: data.vehicle.manufacturer + ' ' + data.vehicle.model + ' [' + data.vehicle.registration + '] has been sold to ' + data.customer.primary_display_as,
                            timestamp: this.data.created_at,
                            actor: data.action_by,
                            seen: !!this.data.seen_at,
                            read: !!this.data.read_at,
                            action: () => {
                                this.$root.navigateTo('deals/' + data.deal_id + '/edit')
                            }
                        }

                    case 'deal.completed':
                        return {
                            title: 'Deal Completed',
                            icon: 'monetization_on',
                            icon_colour: 'success',
                            body: data.vehicle.manufacturer + ' ' + data.vehicle.model + ' [' + data.vehicle.registration + '] has been handed over to ' + data.customer.primary_display_as,
                            timestamp: this.data.created_at,
                            actor: data.action_by,
                            seen: !!this.data.seen_at,
                            read: !!this.data.read_at,
                            action: () => {
                                this.$root.navigateTo('deals/' + data.deal_id + '/edit')
                            }
                        }
                    case 'document.signed':
                        return {
                            title: 'Document Signed',
                            icon: 'check',
                            icon_colour: 'success',
                            body: data.customer.primary_display_as + ' signed the ' + data.document_type.replace(/_/g, ' ') + ' document.',
                            timestamp: this.data.created_at,
                            actor: data.action_by,
                            seen: !!this.data.seen_at,
                            read: !!this.data.read_at,
                            action: () => {
                                if (data.associated_type === 'deal') {
                                    this.$root.navigateTo('deals/' + data.associated_id + '/edit')
                                } else if (data.associated_type === 'reservation') {
                                    this.$root.navigateTo('courtesy-cars/' + data.associated_id)
                                }
                            }
                        }
                    case 'document.declined':
                        return {
                            title: 'Document Rejected',
                            icon: 'close',
                            icon_colour: 'error',
                            body: data.customer.primary_display_as + ' refused to sign the ' + data.document_type.replace(/_/g, ' ') + ' document.',
                            timestamp: this.data.created_at,
                            actor: data.action_by,
                            seen: !!this.data.seen_at,
                            read: !!this.data.read_at,
                            action: () => {
                                if (data.associated_type === 'deal') {
                                    this.$root.navigateTo('deals/' + data.associated_id + '/edit')
                                }
                            }
                        }
                    case 'vehicle.reservation':
                        return {
                            title: 'Vehicle Reserved',
                            icon: 'directions_car',
                            icon_colour: 'info',
                            body: data.vehicle.manufacturer + ' ' + data.vehicle.model + ' [' + data.vehicle.registration + '] has been reserved for ' + data.customer.primary_display_as,
                            timestamp: this.data.created_at,
                            actor: data.action_by,
                            seen: !!this.data.seen_at,
                            read: !!this.data.read_at,
                            action: () => {
                                this.$root.navigateTo('vehicles/' + data.vehicle.instance_id + '/overview')
                            }
                        }
                    case 'accounting.sync.batch.complete':
                        body = (data.totalJobs - data.failedJobs) + ' records transferred to Sage'
                        icon = 'done'
                        icon_colour = 'success'

                        if (data.failedJobs > 0) {
                            body += ', ' + data.failedJobs + ' failed'
                            icon = 'error_outline'
                            icon_colour = 'error'
                        }

                        body += '.'

                        return {
                            title: 'Sage Data Sync Completed',
                            icon,
                            icon_colour,
                            body,
                            timestamp: this.data.finished_at,
                            //actor: data.action_by,
                            seen: !!this.data.seen_at,
                            read: !!this.data.read_at,
                            action: () => {
                                this.$root.navigateTo('money/sync')
                            }
                        }

                    case 'approval.request.revoked':
                    case 'approval.request.approved':
                    case 'approval.request.rejected':
                    case 'approval.request.created':
                        switch (data.type) {
                            case 'approval.request.created':
                                actor = data.requested_by
                                title = 'Approval Requested'
                                icon_colour = 'default'
                                body = data.requested_by.name + ' has requested \'' + data.definition.name
                                    + '\' for ' + data.subject_type + ' #' + data.subject_id;
                                break;
                            case 'approval.request.revoked':
                                actor = data.revoked_by
                                title = 'Approval Revoked'
                                icon_colour = 'warning'
                                body = data.revoked_by.name + ' has revoked \'' + data.definition.name
                                    + '\' for ' + data.subject_type + ' #' + data.subject_id;
                                break;
                            case 'approval.request.approved':
                                actor = data.reviewed_by
                                title = 'Approved'
                                icon_colour = 'success'
                                body = data.reviewed_by.name + ' has approved \'' + data.definition.name
                                    + '\' for ' + data.subject_type + ' #' + data.subject_id;
                                break;
                            case 'approval.request.rejected':
                                actor = data.reviewed_by
                                title = 'Approval Rejected'
                                icon_colour = 'danger'
                                body = data.reviewed_by.name + ' has rejected \'' + data.definition.name
                                    + '\' for ' + data.subject_type + ' #' + data.subject_id;
                                if (data.outcome_reason) {
                                    body += ' because \'' + data.outcome_reason + '\''
                                }
                                break;
                        }

                        return {
                            title,
                            icon: 'approval',
                            icon_colour,
                            body,
                            timestamp: this.data.updated_at,
                            seen: !!this.data.seen_at,
                            read: !!this.data.read_at,
                            actor,
                            action: () => {
                                this.$root.navigateTo('deals/' + data.subject_id + '/edit')
                            }
                        }
                    case 'inbox.reply.received':
                    case 'inbox.new.conversation':
                        return {
                            title: data.type === 'inbox.new.conversation' ? 'New Conversation' : 'New Reply',
                            icon: 'mail',
                            icon_colour: 'info',
                            body: data.type === 'inbox.new.conversation' ?
                                    data.from.primary_display_as + ' created a new conversation with ' + data.message_box.name + '.' :
                                    data.from.primary_display_as + ' replied to your message.',
                            timestamp: this.data.created_at,
                            actor: null,
                            seen: !!this.data.seen_at,
                            read: !!this.data.read_at,
                            action: () => {
                                this.$root.navigateTo('inbox/' + data.message_box.id + '/conversations/' + data.conversation_id)
                            }
                        }
                    case 'inbox.conversation.assigned':
                        return {
                            title: 'Conversation Assigned',
                            icon: 'mail',
                            icon_colour: 'info',
                            body: 'A conversation with ' + data.contact.primary_display_as + ' has been assigned to you.',
                            timestamp: this.data.created_at,
                            actor: data.action_by,
                            seen: !!this.data.seen_at,
                            read: !!this.data.read_at,
                            action: () => {
                                this.$root.navigateTo('inbox/' + data.message_box_id + '/conversations/' + data.conversation_id)
                            }
                        }
                    case 'lead.assigned':
                        return {
                            title: 'Lead Assigned',
                            icon: 'my_location',
                            icon_colour: 'info',
                            body: data.contact.name + ' has been assigned to you.',
                            timestamp: this.data.created_at,
                            actor: data.action_by,
                            seen: !!this.data.seen_at,
                            read: !!this.data.read_at,
                            action: () => {
                                this.$root.navigateTo('leads?title=Custom+View&lead.status=active&order=created_at%20ASC&lead.contact=' + data.contact.id)
                            }
                        }
                    case 'new.lead':
                        return {
                            title: 'New Lead',
                            icon: 'my_location',
                            icon_colour: 'info',
                            body: data.contact.name + ' has been added.',
                            timestamp: this.data.created_at,
                            actor: data.action_by,
                            seen: !!this.data.seen_at,
                            read: !!this.data.read_at,
                            action: () => {
                                this.$root.navigateTo('leads?title=Custom+View&lead.status=active&order=created_at%20ASC&lead.contact=' + data.contact.id)
                            }
                        }
                    case 'funding.warnings':
                        return {
                            title: 'Funding Removals',
                            icon: 'monetization_on',
                            icon_colour: 'warning',
                            body: 'There are ' + data.warning_count + ' funded vehicles that need attention.',
                            timestamp: this.data.created_at,
                            actor: null,
                            seen: !!this.data.seen_at,
                            read: !!this.data.read_at,
                            action: () => {
                                this.$root.navigateTo('finance/funding')
                            }
                        }
                    case 'workflow.notification':
                        return {
                            title: this.data.data.subject,
                            icon: 'notifications',
                            icon_colour: 'info',
                            body: this.data.data.description,
                            timestamp: this.data.created_at,
                            actor: null,
                            seen: !!this.data.seen_at,
                            read: !!this.data.read_at,
                            action: () => {
                                this.$root.goTo(this.data.data.action_url)
                            }
                        }
                    case 'inbox.message.seen':
                        return {
                            title: 'Message Seen',
                            icon: 'mail',
                            icon_colour: 'info',
                            body: `${data.recipient} has seen your message`,
                            timestamp: this.data.created_at,
                            actor: null,
                            seen: !!this.data.seen_at,
                            read: !!this.data.read_at,
                            action: () => {
                                this.$root.navigateTo('inbox/' + data.message_box_id + '/conversations/' + data.conversation_id)
                            }
                        }
                    case 'auto.entry.failed':
                        return {
                            title: 'Auto Entry Failed',
                            icon: 'close',
                            icon_colour: 'error',
                            body: data.filename + ' could not be processed, try again.',
                            timestamp: this.data.created_at,
                            actor: data.action_by,
                            seen: !!this.data.seen_at,
                            read: !!this.data.read_at,
                        }
                    default:
                        throw new Error('Notification type not defined [' + data.type + ']')
                }
            },
        },
        methods: {
            actionSelected(e) {
                let promise

                switch (e.id)
                {
                    case 'mark-unread':
                        promise = this.$store.dispatch('notifications/markUnread', this.data.id)
                        break
                    case 'mark-read':
                        promise = this.$store.dispatch('notifications/markRead', this.data.id)
                        break
                    case 'remove':
                        promise = this.$store.dispatch('notifications/destroy', this.data.id)
                        break
                }

                if (promise) {
                    this.busy = true
                    promise.catch(handleApiError)
                        .finally(() => { this.busy = false })
                }
            },
            clicked(e) {
                console.log(this.$refs)
                if ((
                    (!this.$refs.actionMenu || this.$refs.actionMenu && !this.$refs.actionMenu.$el.contains(e.target)) &&
                    (!this.$refs.actionButton || this.$refs.actionButton && !this.$refs.actionButton.$el.contains(e.target))
                ) && this.notification.action) {
                    this.$store.dispatch('notifications/markRead', this.data.id).finally(() =>{
                        this.notification.action()
                    })
                }
            }
        }
    }
</script>

<style lang="scss" scoped>
    .notification-item {
        position: relative;
        cursor: pointer;
        display: flex;
        padding: 1.25rem 1.5rem .75rem;
        border-top: 1px solid #f0f0f0;

        .notification-item-icon {
            margin-right: 1rem;
        }

        .notification-action-button {
            position: absolute;
            right: 10px;
            top: 50%;
            transform: translateY(-50%);
            display: none;
        }

        &:hover, &.is-active {
            background: #f6f6f6;
            .notification-action-button { display: block; }
        }

        .notification-body {
            flex: 1;
            h1 { font-size: 1.05rem; margin-bottom: 0; }

            .notification-body-text {
                font-size: 14px;
                margin-top: .3rem;
            }

            .notification-meta {
                font-size: 12px; color: #666;
                display: flex;
                align-items: center;
                margin-top: .5rem;

                div { flex: 1; }
                div:last-of-type {
                    text-align: right;
                }

                .notification-avatar-placeholder { height: 24px; }
            }
        }

        .notification-status {
            display: flex;

            div {
                width:10px;
                height: 10px;
                background: #084298;
                border-radius: 50%;
            }
        }

        &.notification-read {
            color: #999;
            .notification-body .notification-meta { color: #999; }
            .notification-status div { background: transparent; }
            //.notification-item-icon { opacity: .5; }
        }

        .notification-handle {
            display: flex;
            align-items: center;
            padding-left: 1rem;
            & > div {
                padding-bottom: 1rem;
            }
        }
    }
</style>